import type { Ref } from 'react'
import type { LucideIcon, LucideProps } from 'lucide-react'

const baseProps = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const

/**
 * Competences — a lightbulb glyph inside the indicator circle.
 *
 * Anatomy:
 *  - Open glass dome (semicircle of r=4.5 centered horizontally on x=12),
 *    its top at y=5.5 and shoulders curving in to (10,15) and (14,15).
 *  - Two horizontal strokes for the screw threads at y=17 (full width) and
 *    y=19 (slightly narrower) — kept as separate open lines instead of a
 *    closed rectangle so the cap reads hollow at icon size (a closed
 *    rectangle that small fills in once the strokes meet).
 *  - Vertically centered around y=12.25, matching the surrounding circle.
 */
export const CompetencesGlyph = function CompetencesGlyph({
  size = 24,
  strokeWidth = 2,
  ref,
  ...props
}: LucideProps & { ref?: Ref<SVGSVGElement> }) {
  return (
    <svg ref={ref} width={size} height={size} strokeWidth={strokeWidth} {...baseProps} {...props}>
      <circle cx="12" cy="12" r="10" fill="none" />
      <path d="M10 15 C 9 14, 7.5 12, 7.5 10 A 4.5 4.5 0 0 1 16.5 10 C 16.5 12, 15 14, 14 15" fill="none" />
      <path d="M10 17 L14 17" fill="none" />
      <path d="M10.5 19 L13.5 19" fill="none" />
    </svg>
  )
} as LucideIcon

/**
 * Values — a classic heart.
 *
 * Geometry:
 *  - Two lobes as exact semicircles (r=3, chord=6) centered at (9,10) and
 *    (15,10). Chord-equals-diameter is what makes the lobes read as fully
 *    rounded; the previous r=3.5 over a 6.5 chord rendered as a flatter
 *    less-than-semicircle.
 *  - Lobes meet at the dip (12,10) tangent-to-vertical on both sides,
 *    producing a clean V cusp pointing down — classic heart top.
 *  - Side cubics drop from each lobe edge (6,10) / (18,10) to the bottom
 *    point (12,18); inner control points (9.5,16) / (14.5,16) put the V
 *    angle at the bottom point at ~78°, the canonical heart opening.
 *  - Outer control points (6,13) / (18,13) keep the curve tangent to the
 *    lobe arcs (vertical) where they meet, so there's no kink.
 */
export const ValuesGlyph = function ValuesGlyph({
  size = 24,
  strokeWidth = 2,
  ref,
  ...props
}: LucideProps & { ref?: Ref<SVGSVGElement> }) {
  return (
    <svg ref={ref} width={size} height={size} strokeWidth={strokeWidth} {...baseProps} {...props}>
      <circle cx="12" cy="12" r="10" fill="none" />
      <path d="M12 18 C 9.5 16, 6 13, 6 10 A 3 3 0 0 1 12 10 A 3 3 0 0 1 18 10 C 18 13, 14.5 16, 12 18 Z" fill="none" />
    </svg>
  )
} as LucideIcon

/**
 * Personality — smile face.
 * The person behind the data. Two filled-dot eyes + a curved smile.
 */
export const PersonalityGlyph = function PersonalityGlyph({
  size = 24,
  strokeWidth = 2,
  ref,
  ...props
}: LucideProps & { ref?: Ref<SVGSVGElement> }) {
  return (
    <svg ref={ref} width={size} height={size} strokeWidth={strokeWidth} {...baseProps} {...props}>
      <circle cx="12" cy="12" r="10" fill="none" />
      <circle cx="9" cy="10" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15" cy="10" r="1.3" fill="currentColor" stroke="none" />
      <path d="M8 14 C 9.5 16.5, 14.5 16.5, 16 14" fill="none" />
    </svg>
  )
} as LucideIcon

/**
 * Global — an isometric globe.
 * Outer sphere outline + a slightly-curved equator (Q-curve dipping ~1.5
 * units below the midline so it reads as the back rim of an equator viewed
 * from above) + a vertical almond meridian. The subtle curve adds the 3D /
 * isometric feel.
 */
export const GlobalGlyph = function GlobalGlyph({
  size = 24,
  strokeWidth = 2,
  ref,
  ...props
}: LucideProps & { ref?: Ref<SVGSVGElement> }) {
  return (
    <svg ref={ref} width={size} height={size} strokeWidth={strokeWidth} {...baseProps} {...props}>
      <circle cx="12" cy="12" r="10" fill="none" />
      <path d="M2 12 Q 12 16.5, 22 12" fill="none" />
      <path
        d="M12 2 C 14.5 4.5, 16 8, 16 12 C 16 16, 14.5 19.5, 12 22 C 9.5 19.5, 8 16, 8 12 C 8 8, 9.5 4.5, 12 2 Z"
        fill="none"
      />
    </svg>
  )
} as LucideIcon
